/**
 * MechaGuard - MissileTower (远程导弹塔)
 *
 * 攻击方式：发射远程制导导弹，自动飞向目标，落点产生范围爆破。
 * 定位：射程最远、单发高伤的范围杀伤重炮，慢射速；克制密集敌群。
 * 满级特性：溅射半径与溅射比例提升（同等离子模式）。
 *
 * 复用 Projectile 的 'homing' 模式 + 溅射，无需改动投射物/游戏核心。
 */

import { Tower } from '../entities/Tower.js';

export class MissileTower extends Tower {
  static META = {
    id: 'missile',
    name: '导弹塔',
    icon: '🚀',
    cost: 180,
    desc: '远程制导导弹，落点范围爆破',
    color: '#ff5577',
    stats: {
      baseRange: 210,        // 全场最远（电磁炮 196）
      baseDamage: 90,         // 单发高伤（等离子 34）
      fireInterval: 2.6,      // 慢射速（等离子 1.2）
      barrelLength: 20,
      turnSpeed: 9,
      firingFx: 0.2,
      projectileSpeed: 360,   // 导弹飞行速度
      splashRadius: 80,       // 大范围溅射（等离子 56）
      splashFactor: 0.5,
      splashRadiusMul: 1.3,  // 满级溅射半径乘数
      splashFactorMul: 1.4,  // 满级溅射比例乘数
    },
  };

  constructor(col, row) {
    super(col, row);
  }

  /** 满级溅射更强 */
  get effectiveSplashRadius() {
    return this.level >= 3 ? this.splashRadius * this.splashRadiusMul : this.splashRadius;
  }

  get effectiveSplashFactor() {
    return this.level >= 3 ? this.splashFactor * this.splashFactorMul : this.splashFactor;
  }

  update(dt, game) {
    this.tickTimers(dt);

    const target = this.findTarget(game.enemies);
    this.target = target;

    if (!target) return;

    this.aimAt(target, dt, this.turnSpeed);

    if (this.cooldown > 0) return;

    this.cooldown = this.fireInterval;
    this.firing = this.firingFx;
    game.sfx('missile');

    const mx = this.x + Math.cos(this.angle) * this.barrelLength;
    const my = this.y + Math.sin(this.angle) * this.barrelLength;

    game.spawnProjectile({
      mode: 'homing',
      x: mx,
      y: my,
      target,
      source: this,
      speed: this.projectileSpeed,
      damage: this.damage,
      splashRadius: this.effectiveSplashRadius,
      splashFactor: this.effectiveSplashFactor,
      color: this.color,
      size: 6,
      shape: 'missile',
      trail: true,
    });

    game.particles.spawnMuzzle(mx, my, this.angle, this.color);
  }

  drawTurret(ctx, accent) {
    // 发射导轨（暗色底）
    ctx.fillStyle = 'rgba(10, 24, 42, 0.92)';
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.rect(-15, -3.5, 33, 7);
    ctx.fill();
    ctx.stroke();

    // 尾部稳定翼（4 片，上下各一片，塔色点缀）—— V2/V3 火箭造型
    ctx.fillStyle = accent;
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(-13, s * 5);
      ctx.lineTo(-2, s * 5);
      ctx.lineTo(-2, s * 11);
      ctx.lineTo(-13, s * 8);
      ctx.closePath();
      ctx.fill();
    }

    // 弹体（白色为主，圆柱 + 圆头）
    const grad = ctx.createLinearGradient(0, -7, 0, 7);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.5, '#eef2f7');
    grad.addColorStop(1, '#c9d3df');
    ctx.fillStyle = grad;
    ctx.strokeStyle = '#9fb0c3';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(-13, -7);
    ctx.lineTo(12, -7);
    ctx.quadraticCurveTo(20, -7, 20, 0); // 前端圆头
    ctx.quadraticCurveTo(20, 7, 12, 7);
    ctx.lineTo(-13, 7);
    ctx.quadraticCurveTo(-16, 7, -16, 0); // 尾部半圆
    ctx.quadraticCurveTo(-16, -7, -13, -7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // 中部识别条（塔色）
    ctx.fillStyle = accent;
    ctx.fillRect(-7, -7, 3, 14);

    // 鼻锥尖端 + 发射口能量指示
    ctx.shadowColor = accent;
    ctx.shadowBlur = 8;
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(20, 0, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // 冷却环（装填进度视觉反馈）
    const progress = Math.max(0, Math.min(1, 1 - this.cooldown / this.fireInterval));
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, 12, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
    ctx.stroke();
  }
}
